import i18n from "../i18n";
import { callAutomationsApi } from "./automations-api";

const t = (key, options) =>
  i18n.t(`coaching-portal:emailAutomations.${key}`, options);

const call = (api, path, method, payload) =>
  callAutomationsApi(api, path, method, payload, t("errors.generic"));

const LIST_URL = "/coaching-portal/custom-workflows/";

// Only screens of this feature: the address comes from the page, so anything else is dropped
const safeReturnTo = (value) =>
  typeof value === "string" && value.startsWith(LIST_URL.slice(0, -1))
    ? value
    : undefined;

const parseJsonField = (value, fallback) => {
  if (value === undefined || value === "") return fallback;
  try {
    return JSON.parse(value);
  } catch {
    throw new Error(t("errors.generic"));
  }
};

// Turning on can fail because something is missing: the API lists what, and the coach sees it
const describeIssues = (error) => {
  if (!error.issues?.length) return error.message;
  const codes = [...new Set(error.issues.map((issue) => issue.code))];
  const labels = codes.map((code) => {
    const label = t(`issues.${code}`);
    return typeof label === "string" ? label : code;
  });
  return `${error.message}: ${labels.join(", ")}`;
};

const Automations = (app) => {
  app
    .post("/automations/create", async ({ api, req, res, flash }) => {
      try {
        const { product, name, triggerKey } = req.body;
        const created = await call(api, "automations", "POST", {
          product,
          name,
          trigger: {
            key: triggerKey,
            settings: parseJsonField(req.body.settings, {}),
          },
          filters: parseJsonField(req.body.filters, {}),
          exitRules: parseJsonField(req.body.exitRules, undefined),
          reentry: req.body.reentry || undefined,
          steps: [],
        });
        return res.redirect(
          `/coaching-portal/custom-workflows/automations/${created.automation._id}`
        );
      } catch (error) {
        flash("error", error.message);
        return res.backDontScroll();
      }
    })
    .post("/automations/from-template", async ({ api, req, res, flash }) => {
      try {
        const { product, keys, all } = req.body;
        // "Add all" names the group; otherwise the templates that were ticked
        const created = await call(api, "automation-templates", "POST", {
          product,
          ...(all ? { all } : { keys: [keys].flat().filter(Boolean) }),
        });
        // One draft opens to review; several are in the list
        if (created.automations.length === 1) {
          return res.redirect(
            `/coaching-portal/custom-workflows/automations/${created.automations[0].automation._id}`
          );
        }
        flash(
          "success",
          t("templates.added", { count: created.automations.length })
        );
        return res.redirect(`${LIST_URL}?access=${product}&view=email`);
      } catch (error) {
        flash("error", error.message);
        return res.backDontScroll();
      }
    })
    .post("/automations/:id/status", async ({ api, req, res, flash }) => {
      const { status, people, backfill } = req.body;
      try {
        await call(api, `automations/${req.params.id}/status`, "POST", {
          status,
          ...(people && { people }),
          ...(backfill && { backfill }),
        });
        flash("success", t(status === "active" ? "turnedOn" : "turnedOff"));
      } catch (error) {
        flash("error", describeIssues(error));
        // The builder then lists what to fix before publishing
        const back = safeReturnTo(req.body.returnTo);
        if (error.issues?.length && back) {
          return res.redirect(`${back.split("?")[0]}?fix=1`);
        }
      }
      res.redirect(safeReturnTo(req.body.returnTo) || LIST_URL);
    })
    .post("/automations/:id/duplicate", async ({ api, req, res, flash }) => {
      try {
        await call(api, `automations/${req.params.id}/duplicate`, "POST", {});
        flash("success", t("duplicated"));
      } catch (error) {
        flash("error", error.message);
      }
      res.backDontScroll();
    })
    .post("/automations/:id/delete", async ({ api, req, res, flash }) => {
      try {
        await call(api, `automations/${req.params.id}`, "DELETE");
        flash("success", t("deleted"));
      } catch (error) {
        flash("error", error.message);
      }
      res.redirect(safeReturnTo(req.body.returnTo) || LIST_URL);
    })
    .post("/automations/:id/save", async ({ api, req, res, flash }) => {
      try {
        const changes = parseJsonField(req.body.changes, {});
        const { apply } = req.body;
        await call(api, `automations/${req.params.id}`, "PUT", {
          ...changes,
          ...(apply && { apply }),
        });
        if (apply) flash("success", t("publish.published"));
        const next = safeReturnTo(req.body.returnTo);
        if (next) return res.redirect(next);
      } catch (error) {
        flash("error", error.message);
      }
      res.backDontScroll();
    })
    .post("/automations/:id/template", async ({ api, req, res, flash }) => {
      try {
        const { name, description, visibility } = req.body;
        await call(api, `automations/${req.params.id}/template`, "POST", {
          name,
          description,
          visibility,
        });
        flash("success", t("saveTemplate.saved"));
      } catch (error) {
        flash("error", error.message);
      }
      res.redirect(safeReturnTo(req.body.returnTo) || LIST_URL);
    })
    .post(
      "/automations/:id/activity/:enrollmentId/remove",
      async ({ api, req, res, flash }) => {
        try {
          await call(
            api,
            `automations/${req.params.id}/activity/${req.params.enrollmentId}/remove`,
            "POST",
            {}
          );
          flash("success", t("activity.removed"));
        } catch (error) {
          flash("error", error.message);
        }
        res.redirect(`${LIST_URL}automations/${req.params.id}?tab=activity`);
      }
    )
    .post("/automations/:id/test", async ({ api, req, res, flash, locals }) => {
      try {
        await call(api, `automations/${req.params.id}/test`, "POST", {
          position: Number(req.body.position),
        });
        flash("success", t("testSent", { email: locals.activeUser?.email }));
      } catch (error) {
        flash("error", error.message);
      }
      res.backDontScroll();
    });
};

export default Automations;
